/**
 * Listas de exclusión de la marca (`/activa/oficial/exclusiones`, 28/10/2026): la gente que nunca tiene que recibir una
 * campaña («Empleados», «VIP que no se tocan»). Se arma una vez, pegando los celulares o los mails (uno por renglón) o
 * subiendo un CSV o un Excel de una columna.
 * - «Vale para todas las campañas» (29/10/2026, prendida por defecto): la lista se aplica sola en toda campaña de la
 *   marca. Apagada, se elige en el bloque «Excluir» del paso Audiencia de cada campaña. La lista de la izquierda dice cuál
 *   es cuál.
 *
 * - Una PÁGINA, no una ventana: la lista de las listas y, al lado, la que se crea o se edita (`?lista=nueva` o
 *   `?lista=<id>` en la URL, así el «atrás» del navegador vuelve a donde estaba).
 * - Editar muestra lo que tiene en el cuadro de texto: lo que queda escrito es la lista (un archivo se suma a eso). El
 *   backend normaliza los celulares como la base de ventas y dice lo que no pudo leer; acá se muestra tal cual.
 * - Borrar pide confirmación en la misma tarjeta y dice qué borradores la tenían elegida: van a frenar en el chequeo
 *   hasta que la saquen.
 * - Del equipo: lleva los celulares completos de esa gente. A una persona de un cliente se le dice por qué no la ve
 *   (`PaginaSoloEquipo`) y el backend igual se la niega.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, FileSpreadsheet, Loader2, Plus, RefreshCw, Save, Trash2, UserX, X } from "lucide-react";
import type { EntradaListaExclusion, ListaExclusion, ListaExclusionDetalle, ListaExclusionGuardada } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { leerArchivo } from "../logic/archivo";
import { renglonesConAlgo, textoContenido, textoDeEntradas, valeSiempre } from "../logic/exclusiones";
import { PaginaSoloEquipo } from "../../campana-nueva/components/SoloEquipo";
import { num, plural } from "../../campana-nueva/utils/format";

const B = "/activa/oficial";
const LARGO_NOMBRE = 60;

export default function ListasExclusionPage() {
  return (
    <PaginaSoloEquipo volverA={`${B}/campanas`} textoVolver="Volver a las campañas">
      <ListasExclusion />
    </PaginaSoloEquipo>
  );
}

/** Lo que se dice después de guardar: qué quedó y lo que no se pudo leer. */
type Aviso = { texto: string; invalidas?: { cantidad: number; ejemplos: string[] } };

function ListasExclusion() {
  const [sp, setSp] = useSearchParams();
  const elegida = sp.get("lista");
  const [listas, setListas] = useState<ListaExclusion[] | null>(null);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState<Aviso | null>(null);

  const cargar = useCallback(async () => {
    setError("");
    try {
      const l = leerRespuesta<ListaExclusion[]>(await oficialApi.listasExclusion());
      if (!l.ok) { setError(l.error || textoError(l)); return; }
      setListas(Array.isArray(l.data) ? l.data : []);
    } catch (e) { setError(textoError(leerError(e))); }
  }, []);
  useEffect(() => { void cargar(); }, [cargar]);

  const ir = (lista: string | null) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    if (lista) n.set("lista", lista);
    else n.delete("lista");
    return n;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to={`${B}/campanas`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft size={13} /> Campañas
          </Link>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight mt-1">Listas de exclusión</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            La gente que nunca tiene que recibir una campaña: empleados, clientes VIP… Armá la lista una vez: vale para todas las
            campañas de la marca. Si la apagás, la elegís en el paso Audiencia de cada campaña, en «Excluir».
          </p>
        </div>
        <button type="button" onClick={() => { setAviso(null); ir("nueva"); }}
          className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700">
          <Plus size={15} /> Nueva lista
        </button>
      </div>

      {aviso && (
        <div className="space-y-1 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          <p className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> {aviso.texto}</p>
          {aviso.invalidas && aviso.invalidas.cantidad > 0 && (
            <p className="flex items-start gap-2 text-amber-800" data-testid="aviso-invalidas">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                {num(aviso.invalidas.cantidad)} {plural(aviso.invalidas.cantidad, "renglón no entró", "renglones no entraron")}: no {plural(aviso.invalidas.cantidad, "es", "son")} un celular ni un mail
                {aviso.invalidas.ejemplos.length ? ` (${aviso.invalidas.ejemplos.map((x) => `«${x}»`).join(", ")}${aviso.invalidas.cantidad > aviso.invalidas.ejemplos.length ? "…" : ""})` : ""}.
              </span>
            </p>
          )}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" aria-label="Las listas de la marca">
          {error ? (
            <div className="text-sm text-red-700" role="alert">
              <p>No pudimos traer las listas. {error}</p>
              <button type="button" onClick={() => void cargar()} className="mt-2 inline-flex items-center gap-1.5 font-semibold hover:underline">
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          ) : listas === null ? (
            <div className="space-y-2" aria-busy="true">{[0, 1].map((i) => <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />)}</div>
          ) : listas.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              <UserX size={22} className="mx-auto mb-2 text-slate-300" />
              Todavía no hay listas. Armá la primera con «Nueva lista».
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {listas.map((l) => (
                <li key={l.id}>
                  <button type="button" onClick={() => { setAviso(null); ir(l.id); }} aria-current={elegida === l.id ? "true" : undefined}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${elegida === l.id ? "bg-violet-50" : "hover:bg-slate-50"}`}>
                    <span className="min-w-0">
                      <span className="block font-semibold text-slate-900">{l.nombre}</span>
                      <span className={`text-[11px] font-semibold ${valeSiempre(l) ? "text-violet-700" : "text-slate-500"}`} data-testid="lista-alcance">
                        {valeSiempre(l) ? "Todas las campañas" : "Se elige por campaña"}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-slate-500">{textoContenido(l)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {elegida ? (
          <EditorLista
            key={elegida}
            id={elegida}
            onCerrar={() => ir(null)}
            onGuardada={(g, nueva) => {
              setAviso({ texto: `Se guardó «${g.lista.nombre}»: ${textoContenido(g.lista)}.`, invalidas: { cantidad: g.invalidas, ejemplos: g.ejemplosInvalidas } });
              void cargar();
              if (nueva) ir(g.lista.id);
            }}
            onBorrada={(nombre, borradores) => {
              setAviso({ texto: `Se borró «${nombre}».${borradores ? ` ${num(borradores)} ${plural(borradores, "borrador la tenía elegida: va a frenar", "borradores la tenían elegida: van a frenar")} en el chequeo hasta que la saquen.` : ""}` });
              void cargar();
              ir(null);
            }}
          />
        ) : (
          <section className="hidden rounded-2xl border border-dashed border-slate-200 p-6 text-sm text-slate-500 lg:block">
            Elegí una lista para verla y editarla, o armá una nueva.
          </section>
        )}
      </div>
    </div>
  );
}

function EditorLista({ id, onCerrar, onGuardada, onBorrada }: {
  id: string;
  onCerrar: () => void;
  onGuardada: (g: ListaExclusionGuardada, nueva: boolean) => void;
  onBorrada: (nombre: string, borradores: number) => void;
}) {
  const nueva = id === "nueva";
  const idNombre = useId();
  const idTexto = useId();
  const idSiempre = useId();
  const entrada = useRef<HTMLInputElement>(null);
  /** «Vale para todas las campañas»: prendida en una lista nueva; en una que se edita, como estaba */
  const [siempre, setSiempre] = useState(true);
  const [detalle, setDetalle] = useState<ListaExclusionDetalle | null>(null);
  const [cargando, setCargando] = useState(!nueva);
  const [nombre, setNombre] = useState("");
  const [texto, setTexto] = useState("");
  const [archivo, setArchivo] = useState<{ nombreArchivo: string; archivo: NonNullable<EntradaListaExclusion["archivo"]> } | null>(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    if (nueva) return;
    let vivo = true;
    void (async () => {
      try {
        const l = leerRespuesta<ListaExclusionDetalle>(await oficialApi.listaExclusion(id));
        if (!vivo) return;
        if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió la lista." : l.error || textoError(l)); return; }
        setDetalle(l.data);
        setNombre(l.data.nombre);
        setTexto(textoDeEntradas(l.data.entradas));
        setSiempre(valeSiempre(l.data));
      } catch (e) {
        if (vivo) setError(textoError(leerError(e)));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [id, nueva]);

  const alElegir = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    try {
      setArchivo({ nombreArchivo: f.name, archivo: await leerArchivo(f) });
    } catch (x) {
      setError((x as Error)?.message || "No pudimos leer el archivo.");
    }
  };

  const nombreLimpio = nombre.trim();
  const motivo = !nombreLimpio ? "Poné un nombre" : nombreLimpio.length > LARGO_NOMBRE ? `El nombre va hasta ${LARGO_NOMBRE} caracteres` : !texto.trim() && !archivo ? "Pegá al menos un celular o un mail, o subí un archivo" : "";

  const guardar = async () => {
    if (motivo) return;
    setGuardando(true);
    setError("");
    try {
      // lo escrito es la lista; el archivo se suma a eso
      const cuerpo: EntradaListaExclusion = { nombre: nombreLimpio, texto, siempre, ...(archivo ? { nombreArchivo: archivo.nombreArchivo, archivo: archivo.archivo } : {}) };
      const l = leerRespuesta<ListaExclusionGuardada>(await (nueva ? oficialApi.crearListaExclusion(cuerpo) : oficialApi.editarListaExclusion(id, cuerpo)));
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió la lista." : l.error || textoError(l)); return; }
      setDetalle(l.data.lista);
      setTexto(textoDeEntradas(l.data.lista.entradas));
      setArchivo(null);
      onGuardada(l.data, nueva);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!detalle) return;
    setBorrando(true);
    setError("");
    try {
      const l = leerRespuesta<{ borrada: true; borradores: number }>(await oficialApi.borrarListaExclusion(id));
      if (!l.ok) { setError(l.error || textoError(l)); return; }
      onBorrada(detalle.nombre, Number(l.data?.borradores) || 0);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setBorrando(false);
    }
  };

  if (cargando) return <section className="h-64 rounded-2xl bg-slate-100 animate-pulse" aria-busy="true" aria-label="Cargando la lista" />;

  const renglones = renglonesConAlgo(texto);
  return (
    <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" aria-label={nueva ? "Nueva lista" : `La lista «${detalle?.nombre || ""}»`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{nueva ? "Nueva lista" : detalle?.nombre || "La lista"}</h3>
        <button type="button" onClick={onCerrar} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Cerrar</button>
      </div>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>}
      {!nueva && !detalle ? null : (
        <>
          <div>
            <label htmlFor={idNombre} className="mb-1 block text-xs font-semibold text-slate-600">Nombre</label>
            <input id={idNombre} value={nombre} maxLength={LARGO_NOMBRE + 20} onChange={(e) => setNombre(e.target.value)} placeholder="Por ejemplo, «Empleados»"
              className="w-full max-w-sm rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label htmlFor={idTexto} className="mb-1 block text-xs font-semibold text-slate-600">Celulares y mails, uno por renglón</label>
            <textarea id={idTexto} value={texto} onChange={(e) => setTexto(e.target.value)} rows={10} spellCheck={false}
              placeholder={"11 5544-3322\n+54 9 351 456-7890\nana@mail.com"}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <p className="mt-1 text-xs text-slate-500">
              {renglones ? `${num(renglones)} ${plural(renglones, "renglón", "renglones")}. ` : ""}
              Los celulares se guardan como en la Base: «11 15 5544-3322» queda +5491155443322, y así se reconoce a la persona aunque esté escrita distinto.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <input ref={entrada} type="file" accept=".csv,.xlsx,.xls" className="hidden" aria-label="Elegir un CSV o un Excel" onChange={(e) => void alElegir(e)} />
            <button type="button" onClick={() => entrada.current?.click()} className="inline-flex items-center gap-1.5 font-semibold text-violet-700 hover:underline">
              <FileSpreadsheet size={14} /> {nueva ? "Subir un CSV o un Excel de una columna" : "Sumar desde un CSV o un Excel"}
            </button>
            {archivo && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700" data-testid="archivo-elegido">
                {archivo.nombreArchivo}: se suma al guardar
                <button type="button" onClick={() => setArchivo(null)} aria-label="Sacar el archivo" className="text-slate-500 hover:text-slate-800"><X size={12} /></button>
              </span>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 px-3 py-2.5" data-testid="vale-siempre">
            <label htmlFor={idSiempre} className="flex items-start gap-2 text-sm">
              <input id={idSiempre} type="checkbox" className="mt-0.5 accent-violet-600" checked={siempre} onChange={(e) => setSiempre(e.target.checked)} />
              <span>
                <span className="font-semibold text-slate-900">Vale para todas las campañas</span>
                <span className="block text-xs text-slate-500">
                  {siempre
                    ? "Nadie de esta lista recibe ninguna campaña de la marca: se saca sola, sin elegirla en cada una."
                    : "Sólo se saca a esta gente de las campañas que la elijan en el paso Audiencia («Excluir»)."}
                </span>
              </span>
            </label>
            {!nueva && detalle && valeSiempre(detalle) !== siempre && (
              <p className="mt-1 text-xs font-semibold text-amber-800">Se aplica al guardar los cambios, también en los borradores que todavía no salieron.</p>
            )}
          </div>

          {!nueva && detalle && detalle.usadaEn.length > 0 && (
            <p className="text-xs text-slate-600">
              La {plural(detalle.usadaEn.length, "tiene elegida un borrador", "tienen elegida estos borradores")}:{" "}
              {detalle.usadaEn.map((c, i) => (
                <span key={c.id}>
                  {i > 0 && ", "}
                  <Link to={`${B}/campanas/${encodeURIComponent(c.id)}/editar?paso=audiencia`} className="font-semibold text-violet-700 hover:underline">{c.nombre}</Link>
                </span>
              ))}
              .
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => void guardar()} disabled={!!motivo || guardando} title={motivo || undefined}
                className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                {guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {nueva ? "Crear la lista" : "Guardar los cambios"}
              </button>
              {motivo && <span className="text-xs text-slate-500">{motivo}</span>}
            </div>
            {!nueva && detalle && !confirmarBorrar && (
              <button type="button" onClick={() => setConfirmarBorrar(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 hover:underline">
                <Trash2 size={14} /> Borrar la lista
              </button>
            )}
          </div>

          {confirmarBorrar && detalle && (
            <div className="space-y-2 rounded-lg bg-red-50 px-3 py-3 text-sm text-red-800" role="group" aria-label="Confirmar que se borra la lista">
              <p>
                ¿Borrar «{detalle.nombre}»? Se vacía y no se puede deshacer.
                {detalle.usadaEn.length > 0 && ` ${plural(detalle.usadaEn.length, "El borrador que la tiene elegida va a frenar", `Los ${num(detalle.usadaEn.length)} borradores que la tienen elegida van a frenar`)} en el chequeo hasta que la saquen.`}
              </p>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => void borrar()} disabled={borrando}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                  {borrando ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Sí, borrarla
                </button>
                <button type="button" onClick={() => setConfirmarBorrar(false)} className="font-semibold hover:underline">No, dejarla</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
